import React, {
	useRef,
	createContext,
	useContext,
	type PropsWithChildren,
	useCallback,
	useMemo,
} from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay/hooks';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import type { Props as AsyncColumnPickerProps } from '@atlassian/jira-issue-table-column-picker/src/ui/main.tsx';
import type { context_nativeIssueTable_IFCConfigContextProvider$key as IFCConfigContextProviderKey } from '@atlassian/jira-relay/src/__generated__/context_nativeIssueTable_IFCConfigContextProvider.graphql';
import type { optimisticUpdatesSelectedOptionsQuery$key as IFCOptimisticUpdatesKey } from '@atlassian/jira-relay/src/__generated__/optimisticUpdatesSelectedOptionsQuery.graphql';
import type { IFCConfigType } from '../types.tsx';
import type { Column } from '../../../types.tsx';
import { COLUMN_ID_INTERNAL_COLUMN } from '../../../constants.tsx';
import { TEMP_CREATE_FIELDSET_ID } from '../column-picker-footer/utils.tsx';
import {
	getSelectedOptions,
	optimisticUpdatesSelectedOptions,
} from '../optimistic-updates/index.tsx';

type IFCConfigContextType = {
	ifcConfig: IFCConfigType;
	addFieldToColumns?: (fieldSetId: string, index?: number, onErrorCallback?: () => void) => void;
	updateSelectedOptions?: (
		fieldId: string,
		type: string,
		options: { id: string; value: string }[],
	) => void;
};

export const IFCConfigContext = createContext<IFCConfigContextType>({ ifcConfig: undefined });

export const IFCConfigContextProvider = ({
	children,
	ifcConfig,
	columns,
	onColumnsChange,
	queryRef,
}: PropsWithChildren<{
	ifcConfig: IFCConfigType;
	columns: Column[];
	onColumnsChange: AsyncColumnPickerProps['onChange'];
	queryRef: IFCConfigContextProviderKey | null;
}>) => {
	const columnRef = useRef(columns);

	const currentIssuesRef = useRef<IFCOptimisticUpdatesKey | null>(null);

	const processedIssuesRef = useRef<{
		issuesRef: IFCOptimisticUpdatesKey | null;
		fieldId: string | null;
	}>({
		issuesRef: null,
		fieldId: null,
	});

	const selectedOptions = useRef<{ [key: string]: string } | undefined>(undefined);

	if (columnRef.current !== columns) {
		columnRef.current = columns;
	}

	const environment = useRelayEnvironment();

	const issuesQueryRef = useFragment<IFCConfigContextProviderKey>(
		graphql`
			fragment context_nativeIssueTable_IFCConfigContextProvider on JiraIssueConnection {
				...optimisticUpdatesSelectedOptionsQuery
			}
		`,
		queryRef,
	);

	if (currentIssuesRef.current !== issuesQueryRef) {
		currentIssuesRef.current = issuesQueryRef;
	}

	const addFieldToColumns = useCallback(
		(fieldSetId: string, index?: number, onErrorCallback?: () => void) => {
			const columnIds = columnRef.current
				?.map(({ id }) => id)
				?.filter(
					(id) => !id.startsWith(COLUMN_ID_INTERNAL_COLUMN) && id !== TEMP_CREATE_FIELDSET_ID,
				);

			const insertIndex = index == null ? columnIds.length : index;
			columnIds.splice(insertIndex, 0, fieldSetId);
			onColumnsChange?.({
				columnIds,
				retries: 3,
				onErrorCallback,
				isHorizontalAutoScrollNeeded: index === undefined,
			});
		},
		[onColumnsChange],
	);

	const updateSelectedOptions = useCallback(
		(fieldId: string, type: string, options: { id: string; value: string }[]) => {
			if (
				processedIssuesRef.current.issuesRef !== currentIssuesRef.current ||
				processedIssuesRef.current.fieldId !== fieldId
			) {
				selectedOptions.current = getSelectedOptions(currentIssuesRef.current, fieldId, type);
				processedIssuesRef.current.issuesRef = currentIssuesRef.current;
				processedIssuesRef.current.fieldId = fieldId;
			}

			if (selectedOptions.current) {
				optimisticUpdatesSelectedOptions(environment, type, selectedOptions.current, options);
			}
		},
		[environment],
	);

	const value = useMemo(
		() => ({
			ifcConfig,
			addFieldToColumns,
			updateSelectedOptions,
		}),
		[ifcConfig, addFieldToColumns, updateSelectedOptions],
	);

	return <IFCConfigContext.Provider value={value}>{children}</IFCConfigContext.Provider>;
};

export const useIFCConfigContext = () => {
	return useContext(IFCConfigContext);
};

export const IFCConfigContextProviderComponent = componentWithCondition(
	() => fg('jira_inline_field_config_gate'),
	IFCConfigContextProvider,
	({ children }: PropsWithChildren<{ ifcConfig: IFCConfigType }>) => children,
);
